import React from 'react';
import { Box, TextField, Checkbox, MenuItem } from '@material-ui/core';
import CreateContentField from './CreateContentField';
import RemoveIcon from '@material-ui/icons/RemoveCircleOutline';
import { SubscriptionDiscount, DiscountValueType, OrderType } from './types';
import { useDebouncedState } from '../useDebouncedState';

const ORDER_TYPE_NONE = '__NONE__';

type Props = {
  discountItem: SubscriptionDiscount;
  index: number;
  errors: any;
  onChange: (
    index: number,
    field: keyof SubscriptionDiscount,
    value: any,
  ) => void;
  onRemove: (index: number) => void;
};

const SubscriptionDiscountRow = React.memo(
  ({ discountItem, index, errors, onChange, onRemove }: Props) => {
    const { value: localDiscountItem, setValue: setLocalDiscountItem } =
      useDebouncedState({
        value: discountItem,
        onChange: (newValue) => {
          Object.keys(newValue).forEach((key) => {
            const field = key as keyof SubscriptionDiscount;
            if (newValue[field] !== discountItem[field]) {
              onChange(index, field, newValue[field]);
            }
          });
        },
        delay: 300,
      });

    const handleChange = (field: keyof SubscriptionDiscount, value: any) => {
      setLocalDiscountItem({ ...localDiscountItem, [field]: value });
    };

    return (
      <div className='content__fields-item'>
        <div className='boost-fields-row'>
          <Box width={'100%'} display={'flex'}>
            {/* ACTIVE */}
            <CreateContentField
              fieldTitle='Active'
              field={
                <Checkbox
                  checked={localDiscountItem.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                />
              }
            />

            {/* TITLE */}
            <CreateContentField
              fieldTitle='Title'
              field={
                <TextField
                  size='small'
                  variant='outlined'
                  value={localDiscountItem.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  error={!!errors?.title}
                  helperText={errors?.title}
                />
              }
            />

            {/* LABEL */}
            <CreateContentField
              fieldTitle='Label'
              field={
                <TextField
                  variant='outlined'
                  size='small'
                  value={localDiscountItem.label}
                  onChange={(e) => handleChange('label', e.target.value)}
                />
              }
            />
            {/* Duration Value Type */}
            <CreateContentField
              fieldTitle='Discount Value Type'
              field={
                <TextField
                  select
                  variant='outlined'
                  size='small'
                  value={localDiscountItem.durationType ?? 'FIRST_PAYMENT'}
                  onChange={(e) =>
                    handleChange(
                      'durationType',
                      e.target.value === ORDER_TYPE_NONE
                        ? undefined
                        : (e.target.value as OrderType),
                    )
                  }
                >
                  <MenuItem value='FIRST_PAYMENT'>FIRST_PAYMENT</MenuItem>
                </TextField>
              }
            />
            {/* Discount Value Type */}
            <CreateContentField
              fieldTitle='Discount Value Type'
              field={
                <TextField
                  select
                  variant='outlined'
                  size='small'
                  value={localDiscountItem.discountValueType ?? 'FIXED_AMOUNT'}
                  onChange={(e) =>
                    handleChange(
                      'discountValueType',
                      e.target.value === ORDER_TYPE_NONE
                        ? undefined
                        : (e.target.value as OrderType),
                    )
                  }
                >
                  <MenuItem value='FIXED_AMOUNT'>FIXED_AMOUNT</MenuItem>
                  <MenuItem value='PERCENT'>PERCENT</MenuItem>
                </TextField>
              }
            />
            {/* SOURCE */}
            <CreateContentField
              fieldTitle='Source'
              field={
                <TextField
                  select
                  variant='outlined'
                  size='small'
                  value={localDiscountItem.source}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('source', e.target.value)
                  }
                >
                  <MenuItem value='ALL'>ALL</MenuItem>
                </TextField>
              }
            />
          </Box>
          <Box width={'100%'} display={'flex'}>
            {/* TOTAL COST */}
            <CreateContentField
              fieldTitle='Total cost'
              field={
                <TextField
                  variant='outlined'
                  type='number'
                  size='small'
                  value={localDiscountItem.totalCost}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('totalCost', e.target.value)
                  }
                  error={!!errors?.totalCost}
                  helperText={errors?.totalCost}
                />
              }
            />

            {/* DISCOUNT */}
            <CreateContentField
              fieldTitle='Discount'
              field={
                <TextField
                  variant='outlined'
                  size='small'
                  value={localDiscountItem.discountValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange('discountValue', e.target.value)
                  }
                  error={!!errors?.discountValue}
                  helperText={errors?.discountValue}
                />
              }
            />

            {/* DELETE */}
            <button
              type='button'
              className='content__delete-btn'
              onClick={() => onRemove(index)}
            >
              <RemoveIcon />
            </button>
          </Box>
        </div>
      </div>
    );
  },
);

export default SubscriptionDiscountRow;
